import React from "react";
import { FaCalendarAlt, FaChevronRight, FaClock, FaMapMarkerAlt, FaUserTie } from "react-icons/fa";
import ChildStatusBadge from "../../shared/components/ChildStatusBadge";
import ChildEmptyState from "../../shared/components/ChildEmptyState";
import {
  canNavigateToClassroom,
  formatSchoolYear,
  resolveCampusName,
  resolveTeacherName,
  resolveTranslatedClassroomName,
} from "../utils/childClassroomUtils";

const DetailRow = ({ icon: Icon, label, value, action }) => (
  <div className="flex items-center gap-3 border-b border-slate-200/80 py-4 last:border-b-0 dark:border-white/10">
    {Icon && <Icon className="shrink-0 text-slate-500 dark:text-slate-400" aria-hidden="true" />}
    <span className="min-w-[120px] text-sm font-medium text-slate-600 dark:text-slate-300">{label}</span>
    <span className="ml-auto min-w-0 break-words text-right text-sm font-semibold text-slate-950 dark:text-white">{value || "-"}</span>
    {action}
  </div>
);

export default function ChildClassroomOverview({
  currentClassroom,
  futureClassroom,
  placement,
  labels,
  onOpenClassroom,
  yearFallback,
  formatDate,
}) {
  const primaryClassroom = futureClassroom || currentClassroom || null;
  const canOpenPrimary = primaryClassroom && canNavigateToClassroom(primaryClassroom) && onOpenClassroom;
  const currentName = currentClassroom ? resolveTranslatedClassroomName(currentClassroom) : labels.noCurrent;
  const futureName = futureClassroom ? resolveTranslatedClassroomName(futureClassroom) : labels.noFuture;
  const teacherName = resolveTeacherName(primaryClassroom);
  const campusName = resolveCampusName(primaryClassroom);

  return (
    <section className="rounded-[20px] border border-slate-200/80 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
      <div className="mb-5 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-200">
          <FaMapMarkerAlt aria-hidden="true" />
        </span>
        <h3 className="text-xl font-bold text-slate-950 dark:text-white">{labels.title}</h3>
      </div>

      <div className="rounded-[18px] border border-sky-100 bg-sky-50/55 p-4 dark:border-sky-400/20 dark:bg-sky-400/10">
        <div className="flex items-start gap-4 border-b border-slate-200/80 pb-4 dark:border-white/10">
          <span className="min-w-[70px] text-sm font-medium text-slate-600 dark:text-slate-300">{labels.status}</span>
          <div>
            <ChildStatusBadge label={placement.label} tone={placement.tone} />
            {placement.helper && <p className="mt-2 text-sm text-slate-500 dark:text-slate-300">{placement.helper}</p>}
          </div>
        </div>

        {!currentClassroom && !futureClassroom && (
          <div className="pt-4">
            <ChildEmptyState title={labels.noCurrent} description={labels.noCurrentDescription} />
          </div>
        )}

        <div className="pt-1">
          <DetailRow
            label={futureClassroom ? labels.futureTitle : labels.currentTitle}
            value={futureClassroom ? futureName : currentName}
            action={
              canOpenPrimary ? (
                <button
                  type="button"
                  onClick={() => onOpenClassroom(primaryClassroom)}
                  className="ml-2 rounded-full p-2 text-slate-600 transition hover:bg-white hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-300 dark:hover:bg-white/10"
                  aria-label={futureClassroom ? labels.futureTitle : labels.currentTitle}
                >
                  <FaChevronRight aria-hidden="true" />
                </button>
              ) : null
            }
          />
          <DetailRow
            icon={FaCalendarAlt}
            label={labels.schoolYear}
            value={primaryClassroom ? formatSchoolYear(primaryClassroom, yearFallback) : "-"}
          />
          <DetailRow
            icon={FaClock}
            label={labels.startDate}
            value={primaryClassroom?.startDate ? formatDate(primaryClassroom.startDate) : "-"}
          />
          <DetailRow icon={FaUserTie} label={labels.teacher} value={teacherName || "-"} />
          <DetailRow
            icon={FaMapMarkerAlt}
            label={labels.campus}
            value={campusName || "-"}
            action={
              campusName ? (
                <span className="ml-2 text-slate-500 dark:text-slate-400">
                  <FaChevronRight aria-hidden="true" />
                </span>
              ) : null
            }
          />
        </div>
      </div>
    </section>
  );
}
